import type { RateLockPriceAdjustment } from '@/components/encompass/prospects/types';

// --- Bucket definitions ---

/** Waterfall bucket names in consumption priority order */
export const WATERFALL_BUCKETS = ['level1', 'branch_management', 'margin'] as const;
export type WaterfallBucket = (typeof WATERFALL_BUCKETS)[number];

/** Map ProfitMargin adjustment descriptions to waterfall buckets */
export const DESCRIPTION_TO_BUCKET: Record<string, WaterfallBucket> = {
  'Level 1': 'level1',
  'Branch Management': 'branch_management',
  Uncategorized: 'margin'
};

/** Descriptions that are never consumed by the waterfall */
const PROTECTED_DESCRIPTIONS = new Set(['LO Compensation']);

// --- Types ---

export interface WaterfallAllocation {
  level1_absorbed: number;
  branch_management_absorbed: number;
  margin_absorbed: number;
}

export interface BucketState {
  bucket: WaterfallBucket;
  label: string;
  original: number;
  consumed: number;
  remaining: number;
}

export interface WaterfallCapacity {
  buckets: BucketState[];
  totalAvailable: number;
  isOverAllocated: boolean;
}

export interface ExistingConcessionAbsorption {
  level1_absorbed: number;
  branch_management_absorbed: number;
  margin_absorbed: number;
}

// --- Core functions ---

/**
 * Extract original bucket capacities from ProfitMargin adjustments.
 * Values are stored as negative rates; we return absolute values.
 */
export function extractBucketCapacities(
  adjustments: RateLockPriceAdjustment[] | undefined | null
): Record<WaterfallBucket, number> {
  const capacities: Record<WaterfallBucket, number> = {
    level1: 0,
    branch_management: 0,
    margin: 0
  };

  if (!adjustments) {
return capacities;
}

  for (const adj of adjustments) {
    if (adj.priceAdjustmentType !== 'ProfitMargin') {
continue;
}
    if (PROTECTED_DESCRIPTIONS.has(adj.description)) {
continue;
}

    const bucket = DESCRIPTION_TO_BUCKET[adj.description];
    if (bucket) {
      capacities[bucket] = Math.abs(adj.rate);
    }
  }

  return capacities;
}

/**
 * Sum already-consumed amounts from existing non-legacy concessions.
 */
export function sumConsumed(
  existingConcessions: ExistingConcessionAbsorption[]
): Record<WaterfallBucket, number> {
  const consumed: Record<WaterfallBucket, number> = {
    level1: 0,
    branch_management: 0,
    margin: 0
  };

  for (const c of existingConcessions) {
    consumed.level1 += c.level1_absorbed;
    consumed.branch_management += c.branch_management_absorbed;
    consumed.margin += c.margin_absorbed;
  }

  return consumed;
}

const BUCKET_LABELS: Record<WaterfallBucket, string> = {
  level1: 'Level 1',
  branch_management: 'Branch Management',
  margin: 'Lendwise Margin'
};

/**
 * Compute current waterfall capacity: original, consumed, remaining per bucket.
 * Remaining is clamped at 0 (handles pricing drift).
 */
export function computeWaterfallCapacity(
  adjustments: RateLockPriceAdjustment[] | undefined | null,
  existingConcessions: ExistingConcessionAbsorption[]
): WaterfallCapacity {
  const originals = extractBucketCapacities(adjustments);
  const consumed = sumConsumed(existingConcessions);

  let isOverAllocated = false;
  const buckets: BucketState[] = WATERFALL_BUCKETS.map((bucket) => {
    const original = originals[bucket];
    const usedAmount = consumed[bucket];
    const remaining = round4(Math.max(0, original - usedAmount));

    if (round4(usedAmount) > round4(original)) {
      isOverAllocated = true;
    }

    return {
      bucket,
      label: BUCKET_LABELS[bucket],
      original,
      consumed: usedAmount,
      remaining
    };
  });

  const totalAvailable = round4(buckets.reduce((sum, b) => sum + b.remaining, 0));

  return { buckets, totalAvailable, isOverAllocated };
}

/**
 * Allocate a concession across the waterfall buckets.
 *
 * Consumes in order: Level 1 → Branch Management → Lendwise Margin.
 * Throws if the concession exceeds remaining capacity.
 * Returns the per-bucket absorption for this concession.
 */
export function allocateWaterfall(
  concessionPoints: number,
  adjustments: RateLockPriceAdjustment[] | undefined | null,
  existingConcessions: ExistingConcessionAbsorption[]
): WaterfallAllocation {
  if (concessionPoints <= 0) {
    throw new Error('Concession points must be greater than 0');
  }

  const capacity = computeWaterfallCapacity(adjustments, existingConcessions);

  if (capacity.isOverAllocated) {
    throw new Error(
      'Existing concessions exceed current pricing capacity. Delete excess concessions or wait for a re-lock before adding new ones.'
    );
  }

  if (concessionPoints > capacity.totalAvailable) {
    throw new Error(
      `Concession of ${concessionPoints.toFixed(4)} points exceeds available capacity of ${capacity.totalAvailable.toFixed(4)} points`
    );
  }

  let remaining = concessionPoints;
  const allocation: WaterfallAllocation = {
    level1_absorbed: 0,
    branch_management_absorbed: 0,
    margin_absorbed: 0
  };

  const allocationKeys: Record<WaterfallBucket, keyof WaterfallAllocation> = {
    level1: 'level1_absorbed',
    branch_management: 'branch_management_absorbed',
    margin: 'margin_absorbed'
  };

  for (const bucketState of capacity.buckets) {
    if (remaining <= 0) {
break;
}

    const absorb = Math.min(remaining, bucketState.remaining);
    allocation[allocationKeys[bucketState.bucket]] = absorb;
    remaining -= absorb;
  }

  // Round to avoid floating-point dust
  allocation.level1_absorbed = round4(allocation.level1_absorbed);
  allocation.branch_management_absorbed = round4(allocation.branch_management_absorbed);
  allocation.margin_absorbed = round4(allocation.margin_absorbed);

  return allocation;
}

/**
 * Preview the waterfall impact of a hypothetical concession.
 * Returns the bucket states after the concession would be applied.
 * Does NOT throw on over-capacity — returns null allocation instead.
 */
export function previewWaterfall(
  concessionPoints: number,
  adjustments: RateLockPriceAdjustment[] | undefined | null,
  existingConcessions: ExistingConcessionAbsorption[]
): { bucketsBefore: BucketState[]; bucketsAfter: BucketState[]; allocation: WaterfallAllocation | null; exceedsCapacity: boolean } {
  const capacity = computeWaterfallCapacity(adjustments, existingConcessions);
  const bucketsBefore = capacity.buckets;

  if (concessionPoints <= 0 || capacity.isOverAllocated || concessionPoints > capacity.totalAvailable) {
    return {
      bucketsBefore,
      bucketsAfter: bucketsBefore,
      allocation: null,
      exceedsCapacity: concessionPoints > capacity.totalAvailable || capacity.isOverAllocated
    };
  }

  const allocation = allocateWaterfall(concessionPoints, adjustments, existingConcessions);

  const allocationKeys: Record<WaterfallBucket, keyof WaterfallAllocation> = {
    level1: 'level1_absorbed',
    branch_management: 'branch_management_absorbed',
    margin: 'margin_absorbed'
  };

  const bucketsAfter: BucketState[] = bucketsBefore.map((b) => ({
    ...b,
    consumed: b.consumed + allocation[allocationKeys[b.bucket]],
    remaining: Math.max(0, b.remaining - allocation[allocationKeys[b.bucket]])
  }));

  return { bucketsBefore, bucketsAfter, allocation, exceedsCapacity: false };
}

// --- Manual allocation validators ---

type ManualValidationResult =
  | { valid: true; allocation: WaterfallAllocation }
  | { valid: false; error: string };

/**
 * Context-free invariant check for an allocation split.
 * Validates nonneg + exact sum (after round4 normalization).
 * Called by: service layer AND UI.
 */
export function validateAllocationInvariants(
  level1: number,
  branchManagement: number,
  margin: number,
  concessionPoints: number
): ManualValidationResult {
  const l1 = round4(level1);
  const bm = round4(branchManagement);
  const m = round4(margin);
  const total = round4(concessionPoints);

  if (l1 < 0 || bm < 0 || m < 0) {
    return { valid: false, error: 'Bucket values must be non-negative' };
  }

  if (round4(l1 + bm + m) !== total) {
    return {
      valid: false,
      error: `Bucket values must sum to ${total.toFixed(4)} points (currently ${round4(l1 + bm + m).toFixed(4)})`
    };
  }

  return {
    valid: true,
    allocation: {
      level1_absorbed: l1,
      branch_management_absorbed: bm,
      margin_absorbed: m
    }
  };
}

/**
 * Capacity-aware validator for a manual allocation split.
 * Delegates invariant checks to validateAllocationInvariants, then
 * checks each bucket value against remaining capacity.
 * Called by: UI only (where pricing context is available).
 */
export function validateManualAllocation(
  level1: number,
  branchManagement: number,
  margin: number,
  concessionPoints: number,
  adjustments: RateLockPriceAdjustment[] | undefined | null,
  existingConcessions: ExistingConcessionAbsorption[]
): ManualValidationResult {
  const invariantResult = validateAllocationInvariants(level1, branchManagement, margin, concessionPoints);
  if (!invariantResult.valid) {
return invariantResult;
}

  const capacity = computeWaterfallCapacity(adjustments, existingConcessions);

  if (capacity.isOverAllocated) {
    return {
      valid: false,
      error: 'Existing concessions exceed current pricing capacity. Delete excess concessions or wait for a re-lock before adding new ones.'
    };
  }

  const values: Record<WaterfallBucket, number> = {
    level1: round4(level1),
    branch_management: round4(branchManagement),
    margin: round4(margin)
  };

  for (const bucketState of capacity.buckets) {
    const val = values[bucketState.bucket];
    if (val > bucketState.remaining) {
      return {
        valid: false,
        error: `${bucketState.label} allocation of ${val.toFixed(4)} exceeds remaining capacity of ${bucketState.remaining.toFixed(4)}`
      };
    }
  }

  return invariantResult;
}

/**
 * Generate before/after bucket states from an explicit manual split.
 * Used by the dialog for live preview in manual mode.
 */
export function previewManualAllocation(
  level1: number,
  branchManagement: number,
  margin: number,
  adjustments: RateLockPriceAdjustment[] | undefined | null,
  existingConcessions: ExistingConcessionAbsorption[]
): { bucketsBefore: BucketState[]; bucketsAfter: BucketState[] } {
  const capacity = computeWaterfallCapacity(adjustments, existingConcessions);
  const bucketsBefore = capacity.buckets;

  const values: Record<WaterfallBucket, number> = {
    level1: round4(level1),
    branch_management: round4(branchManagement),
    margin: round4(margin)
  };

  const bucketsAfter: BucketState[] = bucketsBefore.map((b) => ({
    ...b,
    consumed: b.consumed + values[b.bucket],
    remaining: Math.max(0, b.remaining - values[b.bucket])
  }));

  return { bucketsBefore, bucketsAfter };
}

// --- Helpers ---

export function round4(n: number): number {
  return Math.round(n * 10000) / 10000;
}

/** Convert dollar amount to pricing points */
export function dollarsToPoints(dollars: number, loanAmount: number): number {
  if (loanAmount <= 0) {
return 0;
}
  return round4((dollars / loanAmount) * 100);
}

/** Convert pricing points to dollar amount */
export function pointsToDollars(points: number, loanAmount: number): number {
  return Math.round(((points / 100) * loanAmount) * 100) / 100;
}

/** Convert pricing points to basis points */
export function pointsToBps(points: number): number {
  return round4(points * 100);
}
